<template>
  <!--
  <GmapAutocomplete
    bounds:undefined
    childRefName:"input"
    componentRestrictions:undefined
    fields:null
    options:undefined
    selectFirstOnEnter:true
    types:Array[0]
  >
    https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service
  -->
  <div>
    <div
      class="d-flex"
      :class="showGoogleSuggestionHint ? 'align-baseline' : 'align-center'"
    >
      <div class="checkout-customize__label mr-2">Address</div>
      <div
        class="d-flex font-size-xs px-2"
        :class="
          showGoogleSuggestionHint
            ? 'color-dim-gray'
            : 'background-color-fire-engine-red border-radius color-white'
        "
      >
        <span v-if="invalidAddressMessage">{{
          $options.postmatesAddressStatusMessages.invalid
        }}</span>
        <span v-if="deliveryErrorMessage">{{ cart.deliveryErrorMessage }}</span>
        <span v-if="showGoogleSuggestionHint">{{
          $options.postmatesAddressStatusMessages.invalid
        }}</span>
      </div>
    </div>
    <GmapAutocomplete
      :componentRestrictions="{ country }"
      :fields="['address_components', 'formatted_address']"
      :types="['address']"
      @place_changed="setGoogleSuggestedPlaceChange"
    >
      <template v-slot:input="slotProps">
        <v-text-field
          class="font-size-md-res"
          :class="{
            'google-autocomplete-placeholder-color':
              order.service === 'DELIVERY',
          }"
          ref="input"
          v-on:listeners="slotProps.listeners"
          v-on:attrs="slotProps.attrs"
          :outlined="
            addressState === $options.AddressState.empty || cart.addressError
          "
          :value="order.address.full_address"
          placeholder="+ Add Address"
          @blur="loseFocus"
          @focus="focus = true"
          @input="(val) => setFullAddress(val)"
          dense
          hide-details
        ></v-text-field>
      </template>
    </GmapAutocomplete>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { AddressState } from "@/types/address.js";

const getPlaceNameByType = (addressComponents, type) => {
  const component = addressComponents.find((component) =>
    component.types.includes(type),
  );
  return component ? component.long_name : null;
};

const postmatesAddressStatusMessages = {
  invalid: "Pick from suggested addresses",
  outside: "Out of delivery area",
};

export default {
  name: "GoogleAutocomplete",
  AddressState,
  computed: {
    ...mapState({
      cart: (state) => state.cart,
      order: (state) => state.orders.order,
      country: (state) => state.store.info.address.country,
      user: (state) => state.user.info,
    }),
    deliveryErrorMessage() {
      const deliveryErrorMessage = this.cart.deliveryErrorMessage;
      const deliveryService = this.order.service === "DELIVERY";
      const focusOnAddressField = this.focus;

      // shows delivery address error
      // must NOT have focus
      return (
        deliveryErrorMessage &&
        deliveryService &&
        !focusOnAddressField &&
        this.addressState === AddressState.error
      );
    },
    invalidAddressMessage() {
      const deliveryService = this.order.service === "DELIVERY";
      const focusOnAddressField = this.focus;

      // must NOT have focus
      // must have input
      // and input and selected address do not match
      return (
        deliveryService &&
        !focusOnAddressField &&
        !this.cart.awaitingDeliveryDataUpdate &&
        this.addressState === AddressState.invalid
      );
    },
    showGoogleSuggestionHint() {
      const deliveryService = this.order.service === "DELIVERY";
      const focusOnAddressField = this.focus;

      // must have focus
      // must have input
      // and input and selected address do not match
      return (
        deliveryService &&
        focusOnAddressField &&
        this.addressState === AddressState.invalid
      );
    },
  },
  data() {
    return { focus: false };
  },
  methods: {
    getAddress({ address_components, formatted_address }) {
      const route = this.getAddressComponentByType(address_components, "route");
      const streetNumber = this.getAddressComponentByType(
        address_components,
        "street_number",
      );
      const city = this.getAddressComponentByType(
        address_components,
        "locality",
      );
      const state = this.getAddressComponentByType(
        address_components,
        "administrative_area_level_1",
      );
      const zip = this.getAddressComponentByType(
        address_components,
        "postal_code",
      );

      return {
        address: streetNumber ? `${streetNumber} ${route}` : route,
        city,
        full_address: formatted_address,
        state,
        zip,
      };
    },
    getAddressComponentByType(addressComponents, type) {
      /* return addressComponents.find((component) => component.types[0].includes(type)) */
      /*   .short_name; */
      const component = addressComponents.find((component) =>
        component.types.includes(type),
      );
      return component ? component.short_name : "";
    },
    loseFocus() {
      setTimeout(() => (this.focus = false), 500);
    },
    async setGoogleSuggestedPlaceChange(place) {
      /*
      This event is fired when a PlaceResult is made available for a Place the user has selected.
      If the user enters the name of a Place that was not suggested by the control and presses the
      Enter key, or if a Place Details request fails, the PlaceResult contains the user input in
      the name property, with no other properties defined.
      */
      if (!place.formatted_address) {
        return;
      }

      const address = this.getAddress(place);

      /* this.$store.commit("orders/UPDATE", { */
      /*   name: "lastFormattedAddress", */
      /*   val: address.full_address, */
      /* }); */

      // setOrderAddress has setAddress which also checks address validity
      // but doesn't check on alcohol or tobacco issues!

      await this.setOrderAddress(address);
      await this.$store.dispatch("cart/fetchDeliveryData");
      this.requestSchedulingOptions();

      this.focus = false;
    },
    setOrderAddressComponents(place) {
      const number = getPlaceNameByType(place, "street_number");
      const route = getPlaceNameByType(place, "route");
      const city = getPlaceNameByType(place, "locality");
      const zip = getPlaceNameByType(place, "postal_code");

      const address = {
        ...(route && {
          address: number ? `${number} ${route}` : route,
        }),
        apartment: "",
        ...(city && { city }),
        ...(zip && { zip }),
      };
      this.$store.commit("orders/UPDATE_ADDRESS", address);
    },
  },
  postmatesAddressStatusMessages,
  props: {
    addressState: String,
    openDeliveryAddressStatusDialog: Function,
    requestSchedulingOptions: Function,
    setFullAddress: Function,
    setOrderAddress: Function,
    setVisibilityOfScheduleButtons: Function,
  },
  watch: {
    "order.address.full_address": function (address) {
      !address &&
        this.order.service === "DELIVERY" &&
        this.setVisibilityOfScheduleButtons(false);
    },
    "user.address": function (address) {
      // this is for direct visit from url (mounted doesn't work for this)
      address.full_address && this.setOrderAddress(address);
    },
  },
};
</script>
<style scoped>
.border-radius {
  border-radius: 2px;
}

.v-text-field--outlined >>> fieldset {
  border-color: rgba(226, 27, 35, 1);
}
</style>
